import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import React, { useState } from 'react';

export type Severity = 'success' | 'error' | 'warning' | 'info';

export type Config = {
  message: React.ReactNode;
  severity?: Severity;
  duration?: number;
  closeable?: boolean;
  actionOverride?: React.ReactNode;
};

interface SnackbarContextProps {
  openSnackbar: (config: Config) => void;
  closeSnackbar: () => void;
}

export const SnackbarContext = React.createContext<SnackbarContextProps>({
  openSnackbar: () => {},
  closeSnackbar: () => {},
});

export const useSnackbar = (): SnackbarContextProps => React.useContext(SnackbarContext);

const defaultConfig: Config = {
  message: '',
  severity: 'info',
  duration: 6000,
  closeable: true,
  actionOverride: null,
};

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<Config>(defaultConfig);
  const [open, setOpen] = useState(false);

  const openSnackbar = (newConfig: Config) => {
    setConfig({ ...defaultConfig, ...newConfig });
    setOpen(true);
  };

  const closeSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <SnackbarContext.Provider value={{ openSnackbar, closeSnackbar }}>
      {children}
      <Snackbar
        open={open}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ marginTop: 8 }}
        autoHideDuration={config.duration}
        onClose={closeSnackbar}
      >
        <Alert
          onClose={closeSnackbar}
          severity={config.severity}
          sx={{ minWidth: 500, maxWidth: '50%' }}
          action={config.actionOverride}
        >
          {config.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
